import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import env from '../utils/env';
import { jwtDecode } from 'jwt-decode';

interface SearchLimits {
    hasSearchLimit: boolean;
    dailyLimit: number;
    remainingSearches: number;
    searchesUsed: number;
    resetsAt: Date | null;
}

interface User {
    id: string;
    username: string;
    email: string;
    role: 'ADMIN' | 'USER' | 'FREE' | 'TRIAL';
    isActive: boolean;
    emailVerified?: boolean;
    createdAt?: Date;
    lastLogin?: Date;
    lastLoginAt?: Date;
    updatedAt?: Date;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    subscriptionStatus?: 'active' | 'inactive' | 'canceled' | 'past_due' | 'trialing';
    subscriptionCurrentPeriodEnd?: Date;
    subscriptionTrialEnd?: Date;
    // Trial tracking fields
    trialStartDate?: Date;
    trialEndDate?: Date;
    remainingTrialDays?: number;
    isTrialExpired?: boolean;
    searchLimits?: SearchLimits;
}

interface JWTPayload {
    exp: number;
    iat: number;
    userId: string;
    username: string;
    email: string;
    role: 'ADMIN' | 'USER' | 'FREE' | 'TRIAL';
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (user: User, token: string) => void;
    logout: (reason?: string) => void;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isUser: boolean;
    isFree: boolean;
    isTrial: boolean;
    hasRole: (role: 'ADMIN' | 'USER' | 'FREE' | 'TRIAL') => boolean;
    authLoaded: boolean;
    // Trial and search status
    refreshUserProfile: () => Promise<void>;
    refreshSearchStatus: () => Promise<void>;
    refreshToken: () => Promise<boolean>;
    // Impersonation features
    impersonatedRole: 'ADMIN' | 'USER' | 'FREE' | 'GUEST' | null;
    startImpersonation: (role: 'ADMIN' | 'USER' | 'FREE' | 'GUEST') => void;
    stopImpersonation: () => void;
    isImpersonating: boolean;
    getEffectiveRole: () => 'ADMIN' | 'USER' | 'FREE' | 'TRIAL' | 'GUEST' | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [authLoaded, setAuthLoaded] = useState<boolean>(false);
    const [impersonatedRole, setImpersonatedRole] = useState<'ADMIN' | 'USER' | 'FREE' | 'GUEST' | null>(null);
    const tokenExpiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Check if token is expired
    const isTokenExpired = useCallback((tokenStr: string): boolean => {
        try {
            const decoded = jwtDecode<JWTPayload>(tokenStr);
            const currentTime = Date.now() / 1000;
            return decoded.exp < currentTime;
        } catch (error) {
            console.error('Error decoding token:', error);
            return true;
        }
    }, []);

    // Get time until token expiry
    const getTimeUntilExpiry = useCallback((tokenStr: string): number => {
        try {
            const decoded = jwtDecode<JWTPayload>(tokenStr);
            return (decoded.exp * 1000) - Date.now();
        } catch (error) {
            return 0;
        }
    }, []);

    // Clear all timers
    const clearTimers = useCallback(() => {
        if (tokenExpiryTimeoutRef.current) {
            clearTimeout(tokenExpiryTimeoutRef.current);
            tokenExpiryTimeoutRef.current = null;
        }
        if (tokenRefreshIntervalRef.current) {
            clearInterval(tokenRefreshIntervalRef.current);
            tokenRefreshIntervalRef.current = null;
        }
    }, []);

    // Logout function
    const logout = useCallback((reason?: string) => {
        console.log('🚪 AuthContext: Logging out user', reason ? `(${reason})` : '');
        setUser(null);
        setToken(null);
        setImpersonatedRole(null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        clearTimers();

        // If logout is due to expiry, trigger session expired in App
        if (reason === 'token_expired') {
            window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
        }
    }, [clearTimers]);

    // Refresh token function
    const refreshToken = useCallback(async (): Promise<boolean> => {
        if (!token) return false;

        try {
            console.log('🔄 AuthContext: Refreshing token...');
            const response = await fetch(`${env.apiUrl}/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const newToken = data.token;
                const userData = data.user;

                // Update token and user
                setToken(newToken);
                setUser(userData);
                localStorage.setItem('authToken', newToken);
                localStorage.setItem('user', JSON.stringify(userData));

                // Reset timers with new token
                setupTokenTimers(newToken);

                console.log('✅ AuthContext: Token refreshed successfully');
                return true;
            } else {
                console.error('❌ AuthContext: Failed to refresh token');
                if (response.status === 401) {
                    logout('token_expired');
                }
                return false;
            }
        } catch (error) {
            console.error('❌ AuthContext: Error refreshing token:', error);
            return false;
        }
    }, [token]);

    // Setup token expiry and refresh timers
    const setupTokenTimers = useCallback((tokenStr: string) => {
        clearTimers();

        const timeUntilExpiry = getTimeUntilExpiry(tokenStr);

        if (timeUntilExpiry <= 0) {
            console.log('⏰ Token already expired');
            logout('token_expired');
            return;
        }

        console.log(`⏰ Token will expire in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes`);

        // Set up automatic logout when token expires (24 hours)
        tokenExpiryTimeoutRef.current = setTimeout(() => {
            console.log('⏰ Token expired, logging out');
            logout('token_expired');
        }, timeUntilExpiry);

        // Set up token refresh interval (every 23 hours - 1 hour before expiry)
        const refreshInterval = Math.max(timeUntilExpiry - (60 * 60 * 1000), 30 * 60 * 1000); // At least every 30 minutes

        if (timeUntilExpiry > 60 * 60 * 1000) { // Only set refresh if more than 1 hour until expiry
            tokenRefreshIntervalRef.current = setInterval(async () => {
                console.log('⏰ Attempting to refresh token...');
                const success = await refreshToken();
                if (!success) {
                    clearInterval(tokenRefreshIntervalRef.current!);
                    tokenRefreshIntervalRef.current = null;
                }
            }, refreshInterval);

            console.log(`⏰ Token refresh scheduled for every ${Math.round(refreshInterval / 1000 / 60)} minutes`);
        }
    }, [getTimeUntilExpiry, logout, refreshToken, clearTimers]);

    // Initialize auth state from localStorage on mount
    useEffect(() => {
        console.log('🔄 AuthContext: Initializing authentication...');

        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');

        console.log('   Stored token exists:', !!storedToken);
        console.log('   Stored user exists:', !!storedUser);

        if (storedToken && storedUser) {
            try {
                // Check if token is expired
                if (isTokenExpired(storedToken)) {
                    console.log('   ⏰ Token has expired, clearing authentication');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
                } else {
                    const parsedUser = JSON.parse(storedUser);
                    setToken(storedToken);
                    setUser(parsedUser);
                    console.log('   ✅ Authentication restored from localStorage');
                    console.log('   User role:', parsedUser.role);

                    // Set up token timers
                    setupTokenTimers(storedToken);
                }
            } catch (error) {
                console.error('   ❌ Error parsing stored user data:', error);
                // Clear invalid data
                localStorage.removeItem('authToken');
                localStorage.removeItem('user');
            }
        } else {
            console.log('   ⚠️ No stored authentication found');
        }

        setAuthLoaded(true);
        console.log('   🎯 Authentication loading complete');

        // Cleanup on unmount
        return () => {
            clearTimers();
        };
    }, []);

    const login = async (userData: User, authToken: string) => {
        console.log('🔐 AuthContext: Logging in user:', userData.username);
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('user', JSON.stringify(userData));

        // Set up token timers
        setupTokenTimers(authToken);

        // Refresh profile to get extended trial information
        setTimeout(async () => {
            try {
                const response = await fetch(`${env.apiUrl}/auth/profile/extended`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    const extendedUser = data.user;
                    setUser(extendedUser);
                    localStorage.setItem('user', JSON.stringify(extendedUser));
                    console.log('✅ AuthContext: Extended user profile loaded on login');
                }
            } catch (error) {
                console.error('❌ AuthContext: Error loading extended profile on login:', error);
            }
        }, 100); // Small delay to ensure state is set
    };

    const refreshUserProfile = async () => {
        if (!token) return;

        try {
            const response = await fetch(`${env.apiUrl}/auth/profile/extended`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const updatedUser = data.user;
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                console.log('✅ AuthContext: User profile refreshed with trial data');
            } else if (response.status === 401) {
                console.error('❌ AuthContext: Session expired');
                logout('token_expired');
            } else {
                console.error('❌ AuthContext: Failed to refresh user profile');
            }
        } catch (error) {
            console.error('❌ AuthContext: Error refreshing user profile:', error);
        }
    };

    const refreshSearchStatus = async () => {
        if (!token || !user) return;

        try {
            const response = await fetch(`${env.apiUrl}/auth/search-status`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const updatedUser = { ...user, searchLimits: data.searchStatus };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                console.log('✅ AuthContext: Search status refreshed');
            } else if (response.status === 401) {
                console.error('❌ AuthContext: Session expired');
                logout('token_expired');
            } else {
                console.error('❌ AuthContext: Failed to refresh search status');
            }
        } catch (error) {
            console.error('❌ AuthContext: Error refreshing search status:', error);
        }
    };

    const startImpersonation = (role: 'ADMIN' | 'USER' | 'FREE' | 'GUEST') => {
        // Only allow admins to impersonate
        if (user?.role === 'ADMIN') {
            console.log(`🎭 AuthContext: Starting impersonation as ${role}`);
            setImpersonatedRole(role);
            console.log('🎭 AuthContext: Impersonation state updated');
        } else {
            console.warn('🚫 AuthContext: Impersonation denied - user is not admin');
        }
    };

    const stopImpersonation = () => {
        console.log('🎭 AuthContext: Stopping impersonation');
        setImpersonatedRole(null);
    };

    const getEffectiveRole = (): 'ADMIN' | 'USER' | 'FREE' | 'TRIAL' | 'GUEST' | null => {
        const role = impersonatedRole ? impersonatedRole : user?.role || null;
        console.log('🎭 getEffectiveRole:', { impersonatedRole, userRole: user?.role, effectiveRole: role });
        if (impersonatedRole) {
            return impersonatedRole === 'GUEST' ? 'GUEST' : impersonatedRole;
        }
        return user?.role || null;
    };

    // For GUEST impersonation, treat as unauthenticated
    const isAuthenticated = (impersonatedRole === 'GUEST') ? false : (!!user && !!token);
    const effectiveRole = getEffectiveRole();
    const isImpersonating = !!impersonatedRole;

    // Use effective role for all role checks
    const isAdmin = effectiveRole === 'ADMIN';
    const isUser = effectiveRole === 'USER';
    const isFree = effectiveRole === 'FREE';
    const isTrial = effectiveRole === 'TRIAL';

    const hasRole = (role: 'ADMIN' | 'USER' | 'FREE' | 'TRIAL'): boolean => {
        // For GUEST impersonation, treat as unauthenticated
        if (impersonatedRole === 'GUEST') {
            console.log('🎭 hasRole: GUEST impersonation returns false for', role);
            return false;
        }
        const result = effectiveRole === role;
        // console.log('🎭 hasRole:', { checking: role, effectiveRole, result });
        return result;
    };

    const contextValue: AuthContextType = {
        user,
        token,
        login,
        logout,
        isAuthenticated,
        isAdmin,
        isUser,
        isFree,
        isTrial,
        hasRole,
        authLoaded,
        // Trial and search status
        refreshUserProfile,
        refreshSearchStatus,
        refreshToken,
        // Impersonation features
        impersonatedRole,
        startImpersonation,
        stopImpersonation,
        isImpersonating,
        getEffectiveRole
    };

    console.log('🎭 AuthContext render:', {
        impersonatedRole,
        isImpersonating,
        effectiveRole,
        isAdmin,
        isUser,
        isFree,
        userRole: user?.role
    });

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
export type { AuthContextType, User, SearchLimits };
export { AuthContext };